/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { DynamicFilterConfig, FiltersData, filtersAdvanced } from '@/app/interfaces/filtersAdvanced';
import { filtersBasic } from '@/app/interfaces/filtersBasic';
import { SystemCardComponent } from '@/app/shared/cards/system-card/system-card.component';
import { FiltersComponent } from '@/app/shared/filters/filters.component';
import { SearchBarComponent } from '@/app/shared/search-bar/search-bar.component';
import { TabsComponent } from '@/app/shared/tabs/tabs.component';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, signal, OnInit, NgZone } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { DropdownComponent } from '@/app/shared/dropdown/dropdown.component';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import { DesyAngularModule } from 'desy-angular';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { PaginationComponent } from '@/app/shared/pagination/pagination.component';
import { FiltersService } from '@/app/services/filters.service';
import { lastValueFrom } from 'rxjs';
import { NormalizeDatePipe } from '@/app/pipe/normalizeDate.pipe';
import {
  System,
  SystemsAdvancedFilter,
  SystemsDateRangeFilterValue,
  SystemsListAdvancedParams,
  SystemsListBasicParams,
  SystemsListTabId,
  SystemsListUserData,
  SystemsResponse,
  SystemsWorkflowFilter,
  SystemsWorkflowFrontendRole,
  systemsWorkflowRoleMap,
} from '@/app/interfaces/systems';
import { mapSystemTypeLabel } from '@/app/shared/mappers/system-type-label.mapper';
import { getTaskFunctionLabel } from '@/app/shared/mappers/task-function-label.mapper';
import { DeploySystemReviewRole, DeploySystemReviewStage } from '@/app/interfaces/deploy-system-review';
import { environment } from '@/environments/environment';

type DeploySystemCorrectionRole = 'responsable_IA' | 'jefe_consumidor';

@Component({
  selector: 'app-system-of-ia',
  imports: [
    CommonModule,
    FormsModule,
    DesyAngularModule,
    TabsComponent,
    FiltersComponent,
    SearchBarComponent,
    SystemCardComponent,
    DropdownComponent,
    ButtonsComponent,
    PaginationComponent,
  ],
  templateUrl: './system-of-ia.component.html',
  styleUrl: './system-of-ia.component.scss',
  providers: [NormalizeDatePipe],
})
export class SystemOfIaComponent implements OnInit {
  readonly defaultCardDate = new Date();
  readonly noCreateSystemPermissionMessage = 'No tienes proyectos asociados para crear sistemas de IA';
  readonly noCreateSystemRolePermissionMessage = 'No tienes permisos para crear sistemas de IA';
  private readonly storedUserData: SystemsListUserData = this.getStoredUserData();

  constructor(
    private systemsService: SystemsService,
    private filtersService: FiltersService,
    private ngZone: NgZone,
    private normalizeDate: NormalizeDatePipe
  ) {
    effect(() => {
      const _ = [
        this.currentPage(),
        this.paginationSize(),
        this.orderByName(),
        this.orderDirection(),
        this.search(),
        this.state(),
        this.tipoSistema(),
        this.proyecto(),
        this.tarea(),
        this.creationDate(),
        this.modificationDate(),
        this.selectedTab(),
      ];

      this.cards.set([]);
      this.getSystemsList();
    });
  }

  // VARIABLES

  paginationSize = signal(10);
  currentPage = signal(1);
  hasProjects = signal(true);
  canCreateSystem = computed(() => this.hasProjects() && this.hasCreateSystemRole());
  createSystemPermissionMessage = computed(() => {
    if (!this.hasCreateSystemRole()) {
      return this.noCreateSystemRolePermissionMessage;
    }

    if (!this.hasProjects()) {
      return this.noCreateSystemPermissionMessage;
    }

    return null;
  });
  baseFilters: filtersAdvanced[] = [
    {
      type: 'multiselect',
      label: 'Estado',
      options: [
        { label: 'Solicitado', value: 'PENDING' },
        { label: 'En desarrollo', value: 'APPROVED' },
        { label: 'Pendiente de subsanación', value: 'CORRECTION' },
        { label: 'Desarrollado y revisado', value: 'DEVELOPED_AND_REVIEWED' },
        { label: 'Validado', value: 'VALIDATED' },
        { label: 'Cancelado', value: 'CANCELED' },
        { label: 'Finalizado', value: 'FINALIZED' },
        { label: 'Rechazado', value: 'REJECTED' },
      ],
      selected: '',
    },
    {
      type: 'dateRange',
      label: 'Fecha de creación',
      dateRangeOptions: {
        startDate: '',
        endDate: '',
      },
    },
    {
      type: 'dateRange',
      label: 'Fecha de Modificación',
      dateRangeOptions: {
        startDate: '',
        endDate: '',
      },
    },
  ];

  userLogged = this.storedUserData;

  selectedTab = signal<SystemsListTabId>('allSystems');
  workflowFilter = computed(() => this.getWorkflowFilter());
  tabOptions = computed(() => {
    const tabs: { title: string; url: string; id: SystemsListTabId }[] = [
      { title: 'Todos los sistemas', url: '', id: 'allSystems' },
    ];

    if (this.workflowFilter()) {
      tabs.push({ title: 'Pendientes de aprobar', url: '', id: 'pendingApproval' });
    }

    return tabs;
  });
  orderby: filtersBasic = {
    type: 'dropdown',
    label: 'Ordenar sistemas de IA',
    classes: 'text-sm py-1',
    icon: 'pi pi-sort-alt',
    options: [
      { label: 'Ordenar por más reciente', value: 'recent' },
      { label: 'Ordenar por menos reciente', value: 'oldest' },
      { label: 'Ordenar por última modificación', value: 'update' },
    ],
    selected: '',
  };
  total = signal(0);
  cards = signal<System[]>([]);
  isLoadding = signal(false);
  search = signal('');
  orderByName = signal('fecha_registro');
  orderDirection = signal('desc');

  tipoSistema = signal<string[]>([]);
  proyecto = signal<string[]>([]);
  tarea = signal<string[]>([]);
  creationDate = signal<SystemsDateRangeFilterValue>({});
  state = signal<string[]>([]);
  modificationDate = signal<SystemsDateRangeFilterValue>({});

  // MÉTODOS
  ngOnInit() {
    localStorage.removeItem('selectedFilters');
    this.state.set([]);
    this.getFilters();
  }

  advancedFilters = signal<filtersAdvanced[]>([...this.baseFilters]);
  get params(): [SystemsListBasicParams, SystemsListAdvancedParams] {
    const advancedFilter: SystemsAdvancedFilter = {
      state: this.state(),
      creationDate: this.creationDate(),
      search: this.search(),
      modificationDate: this.modificationDate(),
      tipoSistema: this.tipoSistema(),
      proyecto: this.proyecto(),
      tarea: this.tarea(),
    };

    const jobcode = this.getUserJobCode();
    const serviceManagerOrg = this.storedUserData.roles?.jefe_servicio;
    const workflow = this.workflowFilter();

    if (this.selectedTab() === 'allSystems' && jobcode && !this.isGovernanceOfficeUser()) {
      advancedFilter.jobcode = jobcode;
    }

    if (this.selectedTab() === 'allSystems' && Array.isArray(serviceManagerOrg) && serviceManagerOrg.length > 0) {
      advancedFilter.serviceManagerOrg = serviceManagerOrg.map((orgCode) => this.normalizeWorkflowOrgCode(orgCode));
    }

    if (this.selectedTab() === 'pendingApproval' && workflow) {
      advancedFilter.workflow = workflow;
    }

    return [
      {
        basicFilter: {
          currentPage: this.currentPage(),
          sizePages: this.paginationSize(),
          nameOrderBy: this.orderByName(),
          orderBy: this.orderDirection(),
        },
      },
      {
        advancedFilter,
      },
    ];
  }

  filters = signal<FiltersData>({});

  orgCode = this.storedUserData.orgCode;
  jobCode = this.storedUserData.roleCode;

  async getFilters() {
    const { data } = await lastValueFrom(
      await this.filtersService.getFilters<FiltersData>({
        filter: 'sistema',
        params: {
          ORGCODE: this.orgCode,
          JOBCODE: this.jobCode,
        },
      })
    );

    this.filters.set(data);
    this.setDynamicFilters(data);
  }

  onDropdownChange($event: string) {
    switch ($event) {
      case 'recent':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('desc');
        }
        break;
      case 'oldest':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('asc');
        }
        break;
      case 'update':
        {
          this.orderByName.set('fecha_ultima_modificacion');
          this.orderDirection.set('desc');
        }
        break;
      default:
    }
  }
  handleSearch(value: string) {
    this.search.set(value);
  }

  private getStoredUserData(): SystemsListUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    return JSON.parse(storedUserData) as SystemsListUserData;
  }

  private getUserJobCode(): string | undefined {
    const jobcode = this.storedUserData.roleCode?.trim();

    return jobcode ? jobcode : undefined;
  }

  private hasCreateSystemRole(): boolean {
    const roles = this.storedUserData.roles ?? {};
    const allowedRoles: DeploySystemCorrectionRole[] = ['responsable_IA', 'jefe_consumidor'];

    return allowedRoles.some((role) => Array.isArray(roles[role]) && roles[role].length > 0);
  }

  private getWorkflowFilter(): SystemsWorkflowFilter | undefined {
    const roles = this.storedUserData.roles ?? {};

    for (const [role, org] of Object.entries(roles)) {
      if (this.isGovernanceOfficeRole(role) && Array.isArray(org) && org.length > 0) {
        return {
          role: systemsWorkflowRoleMap[role],
          org: org.map((orgCode) => this.normalizeWorkflowOrgCode(orgCode)),
        };
      }
    }

    return undefined;
  }

  private isGovernanceOfficeUser(): boolean {
    return Object.keys(this.storedUserData.roles ?? {}).some((role) => this.isGovernanceOfficeRole(role));
  }

  private isGovernanceOfficeRole(role: string): role is SystemsWorkflowFrontendRole {
    return role in systemsWorkflowRoleMap;
  }

  private normalizeWorkflowOrgCode(orgCode: string): string {
    const normalizedOrgCode = orgCode.match(/ORG\d+$/);

    return normalizedOrgCode ? normalizedOrgCode[0] : orgCode;
  }

  private isDateRangeFilterValue(value: unknown): value is SystemsDateRangeFilterValue {
    return typeof value === 'object' && value !== null && ('startDate' in value || 'endDate' in value);
  }

  private normalizeReviewState(state?: string | null): string {
    return (state ?? 'PENDING').toUpperCase();
  }

  private buildReviewersState(card: System): string {
    const states = [
      card.technicalReviewerState,
      card.legalReviewerState,
      card.functionalReviewerState,
      card.dpdReviewerState,
    ].map((reviewState) => this.normalizeReviewState(reviewState));

    return states.every((reviewState) => reviewState !== 'PENDING') ? 'APPROVED' : 'PENDING';
  }

  private buildStateGroup(card: System): { group: DeploySystemReviewStage; state: string }[] {
    return [
      { group: 'support', state: this.normalizeReviewState(card.supportGroupState) },
      { group: 'reviewers', state: this.buildReviewersState(card) },
      { group: 'chief', state: this.normalizeReviewState(card.serviceManagerState) },
    ];
  }

  private getUserRolesForExecution(card: System): DeploySystemReviewRole[] {
    const executionOrgs = [card.codigo_organismo, card.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => this.normalizeOrgCodeForEnvironment(org));

    if (!executionOrgs.length) {
      return [];
    }

    return Object.entries(this.storedUserData.roles ?? {})
      .filter(
        ([, orgs]) =>
          Array.isArray(orgs) && orgs.some((org) => executionOrgs.includes(this.normalizeOrgCodeForEnvironment(org)))
      )
      .map(([role]) => role)
      .filter((role): role is DeploySystemReviewRole => this.isDeploySystemReviewRole(role));
  }

  private canCurrentUserReview(card: System): boolean {
    if (!this.isSystemReviewPending(card)) {
      return false;
    }

    const userRoles = this.getUserRolesForExecution(card);
    const activeStage = this.buildStateGroup(card).find((stage) => stage.state === 'PENDING');

    if (!activeStage) {
      return false;
    }

    switch (activeStage.group) {
      case 'support':
        return userRoles.includes('apoyo_OGIA');
      case 'reviewers':
        return this.getReviewerRolesInWorkflowOrder(userRoles).some(
          (role) => this.getReviewStateForUserRole(card, role) === 'PENDING'
        );
      case 'chief':
        return userRoles.includes('jefe_servicio_OGIA');
      default:
        return false;
    }
  }

  private canCurrentUserViewReview(card: System): boolean {
    if ((card.state ?? '').toUpperCase() === 'CORRECTION') {
      return this.isCurrentUserReviewRegistered(card);
    }

    if (!this.isSystemReviewPending(card) || this.normalizeReviewState(card.serviceManagerState) !== 'PENDING') {
      return false;
    }

    return this.getUserRolesForExecution(card).some((role) => {
      if (role === 'jefe_servicio_OGIA') {
        return false;
      }

      const state = this.getReviewStateForUserRole(card, role);

      return state === 'APPROVED' || state === 'CORRECTION';
    });
  }

  private isCurrentUserReviewRegistered(card: System): boolean {
    return this.getUserRolesForExecution(card).some((role) => {
      const state = this.getReviewStateForUserRole(card, role);

      return state === 'APPROVED' || state === 'CORRECTION';
    });
  }

  private canCurrentUserCorrect(card: System): boolean {
    if ((card.state ?? '').toUpperCase() !== 'CORRECTION') {
      return false;
    }

    const executionOrgs = [card.codigo_organismo, card.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => this.normalizeOrgCodeForEnvironment(org));

    if (!executionOrgs.length) {
      return false;
    }

    return Object.entries(this.storedUserData.roles ?? {}).some(([role, orgs]) => {
      return (
        this.isDeploySystemCorrectionRole(role) &&
        orgs.some((org) => executionOrgs.includes(this.normalizeOrgCodeForEnvironment(org)))
      );
    });
  }

  private isDeploySystemReviewPending(card: System): boolean {
    return card.executionSystemId != null && card.state?.toUpperCase() === 'PENDING';
  }

  private isPhase2System(card: System): boolean {
    return card.executionSystemId == null;
  }

  getSystemStateLabel(card: System): string | null {
    const state = card.state?.toUpperCase();
    const isPhase2System = this.isPhase2System(card);

    switch (state) {
      case 'CORRECTION':
        return isPhase2System ? 'Desarrollo en subsanación' : 'Despliegue en subsanación';
      case 'APPROVED':
        return isPhase2System ? 'En desarrollo' : 'Despliegue revisado';
      case 'PENDING':
        return isPhase2System ? 'Desarrollo solicitado' : 'Despliegue solicitado';
      case 'VALIDATED':
        return isPhase2System ? 'Desarrollo validado' : 'Despliegue validado';
      case 'REJECTED':
        return isPhase2System ? 'Desarrollo rechazado' : 'Despliegue rechazado';
      case 'FINALIZED':
        return isPhase2System ? 'Desarrollo finalizado' : 'Despliegue finalizado';
      case 'CANCELED':
        return isPhase2System ? 'Desarrollo cancelado' : 'Despliegue cancelado';
      case 'DEVELOPED_AND_REVIEWED':
        return isPhase2System ? 'Desarrollado y revisado' : 'Despliegue desarrollado y revisado';
      default:
        return null;
    }
  }

  private isSystemReviewPending(card: System): boolean {
    return (
      (this.isPhase2System(card) || this.isDeploySystemReviewPending(card)) && card.state?.toUpperCase() === 'PENDING'
    );
  }

  private getReviewButtonLabel(card: System, canReview: boolean): string {
    if (!canReview) {
      return 'Consultar revisión';
    }

    return this.isPhase2System(card) ? 'Revisar sistema de IA' : 'Revisar sistema desplegado';
  }

  private getReviewButtonUrl(card: System): string {
    const detailsPath = this.isPhase2System(card)
      ? 'catalogo-ia/sistemas-de-ia/detalles/'
      : 'catalogo-ia/sistemas-de-ia/despliegue/detalles/';

    return detailsPath + card.id + '/revision';
  }

  private getReviewerRolesInWorkflowOrder(userRoles: DeploySystemReviewRole[]): DeploySystemReviewRole[] {
    const reviewerRoles: DeploySystemReviewRole[] = ['funcional_OGIA', 'juridico_OGIA', 'tecnico_OGIA', 'dpd_OGIA'];

    return reviewerRoles.filter((role) => userRoles.includes(role));
  }

  private getReviewStateForUserRole(card: System, role: DeploySystemReviewRole): string {
    const stateByRole: Record<DeploySystemReviewRole, string | null | undefined> = {
      apoyo_OGIA: card.supportGroupState,
      funcional_OGIA: card.functionalReviewerState,
      juridico_OGIA: card.legalReviewerState,
      tecnico_OGIA: card.technicalReviewerState,
      dpd_OGIA: card.dpdReviewerState,
      jefe_servicio_OGIA: card.serviceManagerState,
    };

    return this.normalizeReviewState(stateByRole[role]);
  }

  private normalizeOrgCodeForEnvironment(orgCode: string): string {
    return orgCode.startsWith(environment.environment) ? orgCode : `${environment.environment}${orgCode}`;
  }

  private isDeploySystemReviewRole(role: string): role is DeploySystemReviewRole {
    return (
      role === 'apoyo_OGIA' ||
      role === 'funcional_OGIA' ||
      role === 'juridico_OGIA' ||
      role === 'tecnico_OGIA' ||
      role === 'dpd_OGIA' ||
      role === 'jefe_servicio_OGIA'
    );
  }

  private isDeploySystemCorrectionRole(role: string): role is DeploySystemCorrectionRole {
    return role === 'responsable_IA' || role === 'jefe_consumidor';
  }

  private buildCardButtons(card: System) {
    const isPhase2System = this.isPhase2System(card);
    const buttons = [
      {
        label: isPhase2System ? 'Consultar sistema de IA' : 'Consultar sistema desplegado',
        url: isPhase2System
          ? 'catalogo-ia/sistemas-de-ia/detalles/' + card.id
          : 'catalogo-ia/sistemas-de-ia/despliegue/detalles/' + card.id,
      },
    ];
    if (isPhase2System && card.state?.toUpperCase() === 'APPROVED') {
      buttons.unshift({
        label: 'Desplegar sistema de IA',
        url: 'catalogo-ia/sistemas-de-ia/' + card.id + '/desplegar-sistema',
      });
    }
    if (this.canCurrentUserCorrect(card)) {
      buttons.unshift({
        label: isPhase2System ? 'Subsanar sistema de IA' : 'Subsanar despliegue de sistema',
        url: isPhase2System
          ? 'catalogo-ia/sistemas-de-ia/detalles/' + card.id + '/subsanar'
          : 'catalogo-ia/sistemas-de-ia/despliegue/detalles/' + card.id + '/subsanar',
      });
    }
    const canReview = this.canCurrentUserReview(card);
    const canViewReview = this.canCurrentUserViewReview(card);

    if (canReview || canViewReview) {
      buttons.unshift({
        label: this.getReviewButtonLabel(card, canReview),
        url: this.getReviewButtonUrl(card),
      });
    }
    return buttons;
  }
  async getSystemsList() {
    const paramsClear = this.params.map((param) => {
      if ('advancedFilter' in param) {
        const filteredAdvanced = Object.entries(param.advancedFilter)
          .filter(([_, value]) => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'string') return value.trim() !== '';
            if (this.isDateRangeFilterValue(value)) return !!value.startDate || !!value.endDate;
            if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
            return value !== undefined && value !== null;
          })
          .reduce<Partial<SystemsAdvancedFilter>>((acc, [key, value]) => ({ ...acc, [key]: value }), {});
        return { ...param, advancedFilter: filteredAdvanced };
      }
      return param;
    });
    try {
      const systems: SystemsResponse = await this.systemsService.getSystemsList({ params: paramsClear }, this.jobCode);
      this.ngZone.run(() => {
        this.hasProjects.set(systems.data.hasProjects ?? true);
        this.cards.set(
          systems.data.items.map((card) => ({
            ...card,
            buttons: this.buildCardButtons(card),
          }))
        );

        this.total.set(systems.data.total);
      });
    } catch (err) {
      console.error('❌ Error en la llamada.');
      throw err;
    }
  }
  handleApplyFilters(filters) {
    filters.forEach((filtro) => {
      switch (filtro.label) {
        case 'Estado':
          this.state.set(filtro.selected);
          break;
        case 'Tipo de Sistema':
          this.tipoSistema.set(filtro.selected);
          break;
        case 'Proyecto':
          this.proyecto.set(filtro.selected);
          break;
        case 'Tarea':
          this.tarea.set(filtro.selected);
          break;
        case 'Fecha de creación':
          if (filtro.type === 'dateRange' && filtro.dateRangeOptions) {
            this.creationDate.set({
              startDate: this.normalizeDate.transform(filtro.dateRangeOptions.startDate),
              endDate: this.normalizeDate.transform(filtro.dateRangeOptions.endDate),
            });
          }
          break;
        case 'Fecha de Modificación':
          if (filtro.type === 'dateRange' && filtro.dateRangeOptions) {
            this.modificationDate.set({
              startDate: this.normalizeDate.transform(filtro.dateRangeOptions.startDate),
              endDate: this.normalizeDate.transform(filtro.dateRangeOptions.endDate),
            });
          }
          break;
        default:
          break;
      }
    });
    console.log('[APLICAR FILTROS]', filters);
    localStorage.setItem('selectedFilters', JSON.stringify(filters));
  }

  accessionsPaginated = computed(() => {
    const page = this.currentPage();
    const size = this.paginationSize();
    const start = (page - 1) * size;
    const end = start + size;
    console.log(`Mostrando registros de ${start} a ${end}`);
  });
  setDynamicFilters(data: FiltersData) {
    const config: Record<string, DynamicFilterConfig> = {
      tipo_sistema: {
        label: 'Tipo de Sistema',
        mapFn: (item) => ({ label: mapSystemTypeLabel(item.name), value: item.name }),
      },
      proyecto: { label: 'Proyecto', mapFn: (item) => ({ label: item.name, value: item.name }) },
      tarea: { label: 'Tarea', mapFn: (item) => ({ label: getTaskFunctionLabel(item.name), value: item.name }) },
    };

    const dynamicFilters: filtersAdvanced[] = Object.keys(config)
      .filter((key) => data[key])
      .map((key) => {
        const { label, mapFn } = config[key];
        return { type: 'multiselect', label, options: data[key].map(mapFn), selected: [] };
      });

    this.advancedFilters.set([...this.baseFilters, ...dynamicFilters]);
    console.log('[filtros dinámicos]', this.advancedFilters());
  }

  onTabChange(tabId: string) {
    if (tabId === 'pendingApproval' && !this.workflowFilter()) {
      return;
    }

    if (tabId !== 'allSystems' && tabId !== 'pendingApproval') {
      return;
    }

    this.selectedTab.set(tabId);

    switch (tabId) {
      case 'allSystems':
        this.state.set([]);
        break;
      case 'pendingApproval':
        this.state.set(['PENDING']);
        break;
      default:
        this.state.set([]);
        break;
    }
  }
}
